// @catalog F.006
// Helpers compartidos de /ventas (Bloque 8 plan dashboard_ux_ventas_margenes 2026-05-21).
// Lógica de separación POS/vendor + re-rank, extraída de DashboardVentas.tsx.
import type { StoreMetrics } from "../../services/analytics.service";

// Separa una lista de tiendas en POS (retail) y Vendedores (SO).
// Heurística:
//   - storeType="pos" → POS
//   - storeType="vendor" → vendor
//   - sin storeType:
//       * nombre incluye "(Vendedor)" o "(Tienda)" → vendor (compat backend viejo)
//       * resto → POS
export function splitStoresByChannel(stores: StoreMetrics[]): {
  storesPOS: StoreMetrics[];
  storesSO: StoreMetrics[];
} {
  const storesPOS = stores.filter(
    (s) =>
      s.storeType === "pos" ||
      (!s.storeType &&
        !s.storeName.includes("(Vendedor)") &&
        !s.storeName.includes("(Tienda)")),
  );
  const storesSO = stores.filter(
    (s) =>
      s.storeType === "vendor" ||
      (!s.storeType &&
        (s.storeName.includes("(Vendedor)") ||
          s.storeName.includes("(Tienda)"))),
  );
  return { storesPOS, storesSO };
}

// Re-rankea una lista por totalSales DESC y asigna ranking 1..N de modo
// independiente por grupo (POS y vendedores compiten dentro de su canal).
export function rerankStores(arr: StoreMetrics[]): StoreMetrics[] {
  return [...arr]
    .sort((a, b) => b.totalSales - a.totalSales)
    .map((s, i) => ({ ...s, ranking: i + 1 }));
}

// Total $ de un grupo de tiendas (helper para summaries de header).
export function sumStoresTotalSales(stores: StoreMetrics[]): number {
  return stores.reduce((sum, s) => sum + s.totalSales, 0);
}

import type { MonthlyMetrics } from "../../services/analytics.service";

const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export function monthNameToIndex(name: string): number {
  const idx = MONTH_NAMES.indexOf(name.toLowerCase());
  return idx === -1 ? 0 : idx;
}

// Total del período (F4): suma de salesAmount de TODOS los meses del rango.
// Honesto independientemente de si el rango es YTD o un rango arbitrario — a
// diferencia del antiguo computeYTDSales, no asume "desde enero".
export function computePeriodTotal(data: MonthlyMetrics[]): number {
  return data.reduce((s, m) => s + m.salesAmount, 0);
}

// Total del período del año anterior (F4): suma del comparativo YoY por-mes que
// provee el backend (previousYearSalesAmount, B3) sobre el rango. Usa el dato
// por-mes —no re-filtra el año en `data`— porque el rango sólo trae sus meses,
// no los del año anterior.
export function computePeriodPrevYear(data: MonthlyMetrics[]): number {
  return data.reduce(
    (s, m) =>
      s +
      (m.previousYearSalesAmount !== undefined ? m.previousYearSalesAmount : 0),
    0,
  );
}

// ¿El rango arranca en enero? (F4 / D20). Determina si el KPI se rotula
// "YTD acumulado" (true: YTD o año-completo) o "Total del período" (false:
// 1M/3M/custom que no arranca en enero).
export function rangeStartsInJanuary(data: MonthlyMetrics[]): boolean {
  if (data.length === 0) return false;
  const sorted = [...data].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return monthNameToIndex(a.month) - monthNameToIndex(b.month);
  });
  const first = sorted[0]!;
  return monthNameToIndex(first.month) === 0;
}

// Ticket promedio = salesAmount / ordersCount. null si ordersCount = 0.
export function computeAvgTicket(m: MonthlyMetrics | null): number | null {
  if (!m || m.ordersCount === 0) return null;
  return m.salesAmount / m.ordersCount;
}

// Ticket promedio del año anterior (mismo mes calendario) usando
// previousYearSalesAmount + previousYearUnitsSold como heurística. Si no hay
// ordersCount del año anterior en el shape, devolvemos null.
export function computeAvgTicketLY(
  m: MonthlyMetrics | null,
): number | null {
  if (!m) return null;
  if (
    m.previousYearSalesAmount === undefined ||
    m.previousYearSalesAmount === 0
  ) {
    return null;
  }
  // Aproximación: el ticket promedio LY se infiere del mismo ratio
  // sales/orders aplicado a previousYearSalesAmount (no disponible orders LY).
  // Backward-compat: si en el futuro el backend expone previousYearOrdersCount,
  // sustituir aquí sin breaking change en KPICardsVentas.
  return m.ordersCount > 0
    ? m.previousYearSalesAmount / m.ordersCount
    : null;
}

// Sparkline de los últimos N meses (default 12) ordenados cronológicamente.
export function buildSparkline(
  data: MonthlyMetrics[],
  pick: (m: MonthlyMetrics) => number,
  lastN = 12,
): Array<{ value: number }> {
  const sorted = [...data].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return monthNameToIndex(a.month) - monthNameToIndex(b.month);
  });
  return sorted.slice(-lastN).map((m) => ({ value: pick(m) }));
}
